import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Axios instance med default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor for å legge til JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor for å håndtere 401 (uautorisert)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('takstmann');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getProfile: () => api.get('/api/auth/profil'),
  updateProfile: (data) => api.put('/api/auth/profil', data),
};

// Eiendom API
export const eiendomAPI = {
  getAll: () => api.get('/api/eiendom'),
  getById: (id) => api.get(`/api/eiendom/${id}`),
  create: (data) => api.post('/api/eiendom', data),
  update: (id, data) => api.put(`/api/eiendom/${id}`, data),
  delete: (id) => api.delete(`/api/eiendom/${id}`),
};

// Bygning API
export const bygningAPI = {
  getByEiendom: (eiendomId) => api.get(`/api/bygning/eiendom/${eiendomId}`),
  getById: (id) => api.get(`/api/bygning/${id}`),
  create: (data) => api.post('/api/bygning', data),
  update: (id, data) => api.put(`/api/bygning/${id}`, data),
  delete: (id) => api.delete(`/api/bygning/${id}`),
};

// Rom API
export const romAPI = {
  getByBygning: (bygningId) => api.get(`/api/rom/bygning/${bygningId}`),
  getById: (id) => api.get(`/api/rom/${id}`),
  getSporsmal: (id) => api.get(`/api/rom/${id}/sporsmal`),
  create: (data) => api.post('/api/rom', data),
  update: (id, data) => api.put(`/api/rom/${id}`, data),
  delete: (id) => api.delete(`/api/rom/${id}`),
  saveSporsmalSvar: (romId, svarData) => api.post(`/api/rom/${romId}/svar`, svarData),
};

// Bygningsdel API
export const bygningsdelAPI = {
  getByBygning: (bygningId) => api.get(`/api/bygningsdel/${bygningId}`),
  getById: (id) => api.get(`/api/bygningsdel/id/${id}`),
  getSporsmal: (id) => api.get(`/api/bygningsdel/id/${id}/sporsmal`),
  create: (data) => api.post('/api/bygningsdel', data),
  saveSporsmalSvar: (bygningsdelId, svarData) => api.post(`/api/bygningsdel/id/${bygningsdelId}/svar`, svarData),
  updateStatus: (id, statusData) => api.patch(`/api/bygningsdel/id/${id}/status`, statusData),
  delete: (id) => api.delete(`/api/bygningsdel/id/${id}`),
  getBilder: (id) => api.get(`/api/bygningsdel/id/${id}/bilder`),
  uploadBilde: (id, formData) => api.post(`/api/bygningsdel/id/${id}/bilder`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Bilde API
export const bildeAPI = {
  getRomBilder: (romId) => api.get(`/api/rom/${romId}/bilder`),
  uploadRomBilde: (romId, formData) => api.post(`/api/rom/${romId}/bilder`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/api/bilder/${id}`),
};

// Rapport API
export const rapportAPI = {
  valider: (eiendomId) => api.get(`/api/validering/${eiendomId}`),
  hent: (eiendomId) => api.get(`/api/rapport/${eiendomId}`),
};

// Takstmann API
export const takstmannAPI = {
  getAll: () => api.get('/api/takstmann'),
  getById: (id) => api.get(`/api/takstmann/${id}`),
  update: (id, data) => api.put(`/api/takstmann/${id}`, data),
  delete: (id) => api.delete(`/api/takstmann/${id}`),
};

// Lisenser API
export const lisenserAPI = {
  getOversikt: () => api.get('/api/lisenser/oversikt'),
  getAll: () => api.get('/api/lisenser'),
  kjop: (data) => api.post('/api/lisenser/kjop', data),
  kjopAbonnement: (data) => api.post('/api/lisenser/kjop-abonnement', data),
  getAbonnementStatus: () => api.get('/api/lisenser/abonnement-status'),
};

// Priser API
export const priserAPI = {
  getAll: () => api.get('/api/priser'),
  update: (data) => api.post('/api/priser', data),
};

// Transaksjoner API
export const transaksjonsAPI = {
  getAll: () => api.get('/api/transaksjoner'),
};

// Annonser API
export const annonserAPI = {
  getAll: () => api.get('/api/annonser'),
  getTilfeldig: () => api.get('/api/annonser/tilfeldig'),
  registrerVisning: (data) => api.post('/api/annonsevisninger', data),
  registrerKlikk: (visningId) => api.post(`/api/annonsevisninger/${visningId}/klikk`),
};

// Admin Annonser API
export const adminAnnonserAPI = {
  create: (data) => api.post('/api/admin/annonsor', data),
  update: (id, data) => api.put(`/api/admin/annonsor/${id}`, data),
  delete: (id) => api.delete(`/api/admin/annonsor/${id}`),
  getStatistikk: (id) => api.get(`/api/admin/annonsor/${id}/statistikk`),
};

export default api;
